import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { sendOnboardingData } from '../utils/webhookService';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiCheckCircle, FiArrowRight, FiUser, FiBuilding, FiPhone, FiMail, FiCheck } = FiIcons;

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    email: '',
    businessType: '',
    phoneCallsPerDay: '',
    averageCallDuration: '',
    interests: [],
    gdprConsent: false,
    marketingConsent: false
  });

  useEffect(() => {
    // Redirect if not authenticated
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  const businessTypes = [
    'Einzelhandel',
    'E-Commerce', 
    'Gastronomie',
    'Dienstleistung',
    'Beratung/Coaching',
    'Gesundheitswesen',
    'Handwerk',
    'Sonstiges'
  ];

  const callVolumeOptions = [
    '1-5 Anrufe pro Tag',
    '6-15 Anrufe pro Tag',
    '16-30 Anrufe pro Tag',
    '31-50 Anrufe pro Tag',
    'Mehr als 50 Anrufe pro Tag'
  ];

  const callDurationOptions = [
    '1-3 Minuten',
    '4-8 Minuten',
    '9-15 Minuten',
    '16-30 Minuten',
    'Mehr als 30 Minuten'
  ];

  const interestOptions = [
    'Automatisierte Terminbuchung',
    'Kundenservice-Chatbot',
    'Lead-Qualifizierung',
    'Bestellannahme',
    'FAQ-Automatisierung',
    'Kundenfeedback-System',
    'Verkaufsunterstützung',
    'Nachfass-Automatisierung'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleInterestChange = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // Prepare data for webhook
      const webhookData = {
        personalInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone
        },
        companyInfo: {
          company: formData.company,
          businessType: formData.businessType
        },
        communicationVolume: {
          phoneCallsPerDay: formData.phoneCallsPerDay,
          averageCallDuration: formData.averageCallDuration
        },
        interests: formData.interests,
        consent: {
          gdprConsent: formData.gdprConsent,
          marketingConsent: formData.marketingConsent
        },
        submissionDate: new Date().toISOString(),
        source: 'login-onboarding',
        userId: user?.userId
      };

      // Send to webhook
      const webhookResult = await sendOnboardingData(webhookData);
      
      if (webhookResult.success) {
        console.log('Onboarding data sent to webhook successfully');
      } else {
        console.error('Failed to send to webhook:', webhookResult.error);
      }

      // Store onboarding data locally as backup
      localStorage.setItem('onboardingData', JSON.stringify(formData));
      
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Submission error:', error);
      alert('Es gab einen Fehler beim Senden. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.firstName && formData.lastName && formData.company && formData.phone && formData.email;
      case 2:
        return formData.businessType && formData.phoneCallsPerDay && formData.averageCallDuration;
      case 3:
        return true; // Interests are optional
      case 4:
        return formData.gdprConsent;
      default:
        return false;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-azure-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Section - Onboarding Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-azure-600 to-azure-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-black bg-opacity-20"></div>
        <div className="relative z-10 flex flex-col justify-center items-center p-12 text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <img
              src="https://github.com/mike3011/Bilder/blob/main/3.png?raw=true"
              alt="K³ Logo"
              className="h-24 w-auto mx-auto mb-8 filter brightness-0 invert"
            />
            <h1 className="text-4xl font-bold mb-6">Lass uns starten!</h1>
            <p className="text-xl text-azure-100 mb-12 max-w-md leading-relaxed">
              Wir richten alles für dich ein. Ein paar kurze Fragen helfen uns dabei, die perfekte KI-Lösung für dein Unternehmen zu finden.
            </p>
            <div className="space-y-6">
              <div className="flex items-center text-azure-100">
                <SafeIcon icon={FiTarget} className="text-2xl mr-4" />
                <span>Personalisierte Einrichtung</span>
              </div>
              <div className="flex items-center text-azure-100">
                <SafeIcon icon={FiCheckCircle} className="text-2xl mr-4" />
                <span>Maßgeschneiderte Lösungen</span>
              </div>
              <div className="flex items-center text-azure-100">
                <SafeIcon icon={FiArrowRight} className="text-2xl mr-4" />
                <span>Schneller Start</span>
              </div>
            </div>

            {/* Progress Indicator */}
            <div className="mt-12">
              <div className="flex justify-center space-x-4">
                {[1, 2, 3, 4].map((step) => (
                  <div
                    key={step}
                    className={`w-3 h-3 rounded-full ${
                      step <= currentStep ? 'bg-white' : 'bg-white bg-opacity-30'
                    }`}
                  />
                ))}
              </div>
              <p className="text-azure-100 text-sm mt-2">
                Schritt {currentStep} von 4
              </p>
            </div>
          </motion.div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-white bg-opacity-10 rounded-full"></div>
        <div className="absolute bottom-20 left-20 w-24 h-24 bg-white bg-opacity-10 rounded-full"></div>
      </div>

      {/* Right Section - Onboarding Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="w-full max-w-md"
        >
          {/* Mobile Header */}
          <div className="lg:hidden text-center mb-8">
            <img
              src="https://github.com/mike3011/Bilder/blob/main/3.png?raw=true"
              alt="K³ Logo"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold text-black">Setup</h1>
            <p className="text-gray-600">Lass uns dein Profil einrichten</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8">
            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex justify-between mb-2">
                <span className="text-sm text-gray-600">Schritt {currentStep} von 4</span>
                <span className="text-sm text-gray-600">{Math.round((currentStep / 4) * 100)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-azure-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(currentStep / 4) * 100}%` }}
                />
              </div>
            </div>

            {/* Step 1: Personal Information */}
            {currentStep === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-black mb-2">Persönliche Daten</h2>
                  <p className="text-gray-600">Lass uns dich kennenlernen</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vorname *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                      placeholder="Max"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nachname *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                      placeholder="Mustermann"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unternehmen *
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiBuilding} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                      placeholder="Ihr Unternehmen"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefonnummer *
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiPhone} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                      placeholder="+49 123 456789"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    E-Mail-Adresse *
                  </label>
                  <div className="relative">
                    <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
                      placeholder="ihre.email@beispiel.de"
                      required
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Business Information & Communication Volume */}
            {currentStep === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-black mb-2">Ihr Unternehmen</h2>
                  <p className="text-gray-600">Erzählen Sie uns von Ihrem Business</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Branche *
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {businessTypes.map((type) => (
                      <label
                        key={type}
                        className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                          formData.businessType === type
                            ? 'border-azure-500 bg-azure-50'
                            : 'border-gray-300 hover:border-azure-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="businessType"
                          value={type}
                          checked={formData.businessType === type}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <span className="text-sm font-medium">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Anzahl Telefonate pro Tag *
                  </label>
                  <div className="space-y-2">
                    {callVolumeOptions.map((option) => (
                      <label
                        key={option}
                        className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                          formData.phoneCallsPerDay === option
                            ? 'border-azure-500 bg-azure-50'
                            : 'border-gray-300 hover:border-azure-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="phoneCallsPerDay"
                          value={option}
                          checked={formData.phoneCallsPerDay === option}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <span className="text-sm">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Durchschnittliche Gesprächsdauer *
                  </label>
                  <div className="space-y-2">
                    {callDurationOptions.map((option) => (
                      <label
                        key={option}
                        className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                          formData.averageCallDuration === option
                            ? 'border-azure-500 bg-azure-50'
                            : 'border-gray-300 hover:border-azure-300'
                        }`}
                      >
                        <input
                          type="radio"
                          name="averageCallDuration"
                          value={option}
                          checked={formData.averageCallDuration === option}
                          onChange={handleInputChange}
                          className="sr-only"
                        />
                        <span className="text-sm">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Interests */}
            {currentStep === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-black mb-2">Interessensgebiete</h2>
                  <p className="text-gray-600">Was interessiert Sie? (optional, mehrfach auswählbar)</p>
                </div>

                <div className="space-y-2">
                  {interestOptions.map((option) => (
                    <label
                      key={option}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                        formData.interests.includes(option)
                          ? 'border-azure-500 bg-azure-50'
                          : 'border-gray-300 hover:border-azure-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.interests.includes(option)}
                        onChange={() => handleInterestChange(option)}
                        className="sr-only"
                      />
                      <SafeIcon 
                        icon={FiCheck} 
                        className={`text-azure-600 mr-3 ${
                          formData.interests.includes(option) ? 'opacity-100' : 'opacity-0'
                        }`} 
                      />
                      <span className="text-sm">{option}</span>
                    </label>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 4: GDPR Consent */}
            {currentStep === 4 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-black mb-2">Datenschutz & Einverständnis</h2>
                  <p className="text-gray-600">Ihre Zustimmung nach DSGVO</p>
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-black mb-2">Datenverarbeitung</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Wir verarbeiten Ihre personenbezogenen Daten zur Bereitstellung unserer KI-Beratungsleistungen. 
                      Ihre Daten werden vertraulich behandelt und nicht an Dritte weitergegeben.
                    </p>
                    <label className="flex items-start">
                      <input
                        type="checkbox"
                        name="gdprConsent"
                        checked={formData.gdprConsent}
                        onChange={handleInputChange}
                        className="mt-1 mr-3 h-4 w-4 text-azure-600 focus:ring-azure-500 border-gray-300 rounded"
                        required
                      />
                      <span className="text-sm text-gray-700">
                        Ich stimme der Verarbeitung meiner personenbezogenen Daten gemäß der{' '}
                        <button
                          type="button"
                          className="text-azure-600 hover:text-azure-700 underline"
                          onClick={() => window.open('#', '_blank')}
                        >
                          Datenschutzerklärung
                        </button>
                        {' '}zu. *
                      </span>
                    </label>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-black mb-2">Marketing-Kommunikation (optional)</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Möchten Sie über neue KI-Lösungen und Updates informiert werden?
                    </p>
                    <label className="flex items-start">
                      <input
                        type="checkbox"
                        name="marketingConsent"
                        checked={formData.marketingConsent}
                        onChange={handleInputChange}
                        className="mt-1 mr-3 h-4 w-4 text-azure-600 focus:ring-azure-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">
                        Ja, ich möchte über KI-Neuigkeiten und Angebote per E-Mail informiert werden. 
                        Diese Einwilligung kann jederzeit widerrufen werden.
                      </span>
                    </label>
                  </div>

                  <div className="text-xs text-gray-500 bg-blue-50 p-3 rounded-lg">
                    <p className="mb-2">
                      <strong>Ihre Rechte:</strong> Sie haben das Recht auf Auskunft, Berichtigung, 
                      Löschung und Einschränkung der Verarbeitung Ihrer Daten.
                    </p>
                    <p>
                      <strong>Kontakt:</strong> Bei Fragen zum Datenschutz wenden Sie sich an:{' '}
                      <a href="mailto:mike.trepte@ki-kundenkommunikation.de" className="text-azure-600">
                        mike.trepte@ki-kundenkommunikation.de
                      </a>
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              <button
                onClick={prevStep}
                disabled={currentStep === 1}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Zurück
              </button>

              {currentStep < 4 ? (
                <button
                  onClick={nextStep}
                  disabled={!isStepValid()}
                  className="px-6 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Weiter
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={!isStepValid() || isSubmitting}
                  className="px-6 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Wird gesendet...
                    </span>
                  ) : (
                    'Abschließen'
                  )}
                </button>
              )}
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Nach dem Abschluss wirst du zu deinem Dashboard weitergeleitet
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default OnboardingPage;